import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { format } from 'date-fns'

import RightCampaignsTable from './RightCampaignsTable'
import RightCampaignsListActions from './RightCampaignsListActions'
import PageSectionHeader from '../../../../features/components/PageSectionHeader'
import PartnerbrainOptimisations from '../../../../forms/Multiplatform/PartnerbrainOptimisations'

import { groupCampaignsByProvider } from './helpers'
import { useDeleteCampaignAction } from '../../../../features/hooks/useDeleteAction/useDeleteCampaignAction'

import {
  getCombinedCampaigns,
  getCombinedCampaignsSuccess,
  clearCombinedCampaigns
} from '../../../../modules/actions/combinedData'
import { rightSelector, rightWasLoadedSelector } from '../../../../modules/selectors/rights'
import {
  combinedCampaignsWasLoadedSelector,
  combinedDataDateRangeSelector
} from '../../../../modules/selectors/combinedData'
import { campaignDeleteItemSelector, campaignWasDeletedSelector } from '../../../../modules/selectors/campaigns'
import { updateRight } from '../../../../modules/actions/rights'
import { clearDeleteCampaign } from '../../../../modules/actions/campaigns'

import { DATES_FORMAT_BE } from '../../../../constants/dates'

const RightCampaigns = () => {
  const dispatch = useDispatch()

  const right = useSelector(rightSelector)
  const rightWasLoaded = useSelector(rightWasLoadedSelector)
  const combinedCampaignsWasLoaded = useSelector(combinedCampaignsWasLoadedSelector)
  const combinedDataDateRange = useSelector(combinedDataDateRangeSelector)
  const { date_preset, startDate, endDate } = combinedDataDateRange
  const campaignWasDeleted = useSelector(campaignWasDeletedSelector)
  const campaignDeleteItem = useSelector(campaignDeleteItemSelector)

  const { campaigns: rightCampaignsToLoad } = right

  const hasRightCampaignsToLoad = !!rightCampaignsToLoad && !!rightCampaignsToLoad.length

  const isCampaignsLoadingSkipped = rightWasLoaded && !hasRightCampaignsToLoad

  const [campaignsCheckboxes, setCampaignsCheckboxes] = useState({})

  const selectedCampaigns = useMemo(
    () => Object.keys(campaignsCheckboxes).filter(key => campaignsCheckboxes[key]),
    [campaignsCheckboxes]
  )

  const setSelectedCampaignsHandler = useCallback(updatedSelectedCampaigns => {
    setCampaignsCheckboxes(updatedSelectedCampaigns)
  }, [])

  const loadCampaigns = useCallback(() => {
    const campaignsGroupedByProvider = groupCampaignsByProvider(rightCampaignsToLoad)

    dispatch(
      getCombinedCampaigns({
        insights: [
          'impressions',
          'cpm',
          'cpa',
          'ctr',
          'clicks',
          'cpc',
          'reach',
          'engagement',
          'frequency',
          'spend',
          'purchase',
          'roas',
          'conversions',
          'lead',
          'results',
          'video_view_rate',
          'video_views'
          // todo PBR-1057 uncomment when failing request is fixed
          // 'complete_registration',
          // 'landing_page_view'
        ],
        date_from: startDate && format(startDate, DATES_FORMAT_BE),
        date_to: endDate && format(endDate, DATES_FORMAT_BE),
        date_preset,
        providers: campaignsGroupedByProvider
      })
    )
  }, [dispatch, startDate, endDate, date_preset, rightCampaignsToLoad])

  // if we don't need to load campaigns, call success action with empty state to mock finished loading
  useEffect(() => {
    if (isCampaignsLoadingSkipped) {
      dispatch(getCombinedCampaignsSuccess({}))
    }
  }, [dispatch, isCampaignsLoadingSkipped])

  useEffect(() => {
    // load initial campaigns
    if (hasRightCampaignsToLoad && !combinedCampaignsWasLoaded) {
      loadCampaigns()
    }
  }, [hasRightCampaignsToLoad, loadCampaigns, combinedCampaignsWasLoaded])

  useEffect(() => {
    // custom useEffect to re-load campaigns when date filter changes
    if (combinedCampaignsWasLoaded && hasRightCampaignsToLoad) {
      loadCampaigns()
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, date_preset])

  useEffect(
    () => () => {
      dispatch(clearCombinedCampaigns())
    },
    [dispatch]
  )

  useEffect(() => {
    // When delete campaign - update the right with new campaigns list,
    // it will trigger re-fetch of reports
    if (campaignWasDeleted) {
      const { campaigns: rightCampaigns } = right
      const { id: campaignDeleteId } = campaignDeleteItem

      const updatedRightCampaigns = rightCampaigns.filter(campaign => campaign.external_id !== campaignDeleteId)
      dispatch(
        updateRight(right.id, {
          campaigns: updatedRightCampaigns
        })
      )

      dispatch(clearDeleteCampaign())
    }
  }, [dispatch, campaignWasDeleted, campaignDeleteItem, right, campaignDeleteItem.id, campaignDeleteItem.isLoading])

  const { handleDeleteRequest, DeleteCampaignModal } = useDeleteCampaignAction({ updateCombinedCampaignsList: true })

  return (
    <section id="right-campaigns-table">
      <PageSectionHeader
        title="List of Campaigns"
        RightPanel={<RightCampaignsListActions selectedCampaigns={selectedCampaigns} />}
      />
      <RightCampaignsTable onRowCheck={setSelectedCampaignsHandler} handleDeleteRequest={handleDeleteRequest} />
      {DeleteCampaignModal}
      <PartnerbrainOptimisations />
    </section>
  )
}

export default RightCampaigns
